import cx from 'classnames'
import { useState } from 'react'
import dayjs, { Dayjs } from 'dayjs'

import Styles from './PostFrequency.module.scss'

import { Text } from 'blaze-ui/Text/Text'
import { PostingCadence } from '../../../../ScheduleContent'
import {
  cadenceToDays,
  cadenceToDisplay,
  DayAndTime,
  firstAvailableTime,
  isToday,
  TimeOfDay,
} from '../../StartDateAndPostFrequencyModal'
import { Check2 } from 'blaze-ui/icons'
import { Button } from 'blaze-ui/Button'
import { Pressable } from '@react-aria/interactions'
import { ChevronRight } from 'blaze-ui/icons/24'
import { CustomSchedule } from '../CustomSchedule/CustomSchedule'
import { Drawer } from 'components/Drawer'
import { useRootStore } from 'stores/RootStore'

interface Props {
  startDate: Dayjs
  initialPostingCadence: PostingCadence
  initialTimeOfDay: TimeOfDay
  initialDays: DayAndTime[]
  customTime: string | undefined
  send: (action: string) => void
  onConfirm: (postingCadence: PostingCadence, days: DayAndTime[]) => void
  onScheduleChange: (schedule: { startDate: Dayjs; daysAndTimes: DayAndTime[]; isCustom: boolean }) => void
  isCreatingCustomSchedule: boolean
  setIsCreatingCustomSchedule: (val: boolean) => void
}

export const PostFrequency = ({
  startDate,
  initialPostingCadence,
  initialTimeOfDay,
  initialDays,
  customTime,
  send,
  onConfirm,
  onScheduleChange,
  isCreatingCustomSchedule,
  setIsCreatingCustomSchedule,
}: Props) => {
  const [postingCadence, setPostingCadence] = useState(initialPostingCadence)
  const [days, setDays] = useState(initialDays)
  const [timeOfDay, setTimeOfDay] = useState(initialTimeOfDay)
  const [customDays, setCustomDays] = useState<DayAndTime[]>([])
  const drawersStore = useRootStore().drawersStore

  const openCustomSchedule = (newDays: DayAndTime[]) => {
    send('CHOOSE_SCHEDULE')
    setCustomDays(newDays)
    setIsCreatingCustomSchedule(true)
  }

  const handleConfirm = () => {
    onConfirm(postingCadence, days)
    send('GO_BACK')
  }

  if (isCreatingCustomSchedule) {
    return (
      <CustomSchedule
        initialDays={customDays}
        customTime={customTime}
        startDate={startDate}
        onConfirm={(cadence, days) => {
          onScheduleChange({ startDate, daysAndTimes: days, isCustom: cadence === PostingCadence.Custom })
          drawersStore.closeDrawer()
        }}
      />
    )
  }

  return (
    <>
      <Drawer.Container>
        <div className={Styles.container}>
          <Text variant="placeholder" color="var(--dark-80)">
            Select when you would like to post
          </Text>
          <div className={Styles.cadencesContainer}>
            {Object.entries(PostingCadence).map(([_key, value], index) => {
              const cadenceDisplay = cadenceToDisplay(value as PostingCadence, startDate)

              return (
                <Pressable
                  key={value}
                  onPress={() => {
                    let ofDay = timeOfDay
                    if (isToday(startDate)) {
                      ofDay = firstAvailableTime(dayjs().hour())
                      setTimeOfDay(ofDay)
                    }
                    setPostingCadence(value)
                    const newDays = cadenceToDays(value, ofDay, startDate) as DayAndTime[]
                    setDays(newDays)
                    if (value === PostingCadence.Custom) {
                      openCustomSchedule(newDays)
                    }
                  }}
                >
                  <div
                    className={cx(Styles.cadenceContainer, {
                      [Styles.bottomBorder]: index !== Object.keys(PostingCadence).length - 1,
                      [Styles.topBorderRadius]: index === 0,
                      [Styles.bottomBorderRadius]: index === Object.keys(PostingCadence).length - 1,
                    })}
                  >
                    <Text variant="largeList">{cadenceDisplay.text}</Text>
                    {PostingCadence.Custom !== value ? (
                      <div className={Styles.cadenceDaysContainer}>
                        <span className={Styles.cadenceDays}>{cadenceDisplay.days}</span>
                        {postingCadence === value && <Check2 size={16} color="var(--selected-stroke)" />}
                      </div>
                    ) : (
                      <div className={Styles.cadenceDaysContainer}>
                        <ChevronRight size={16} />
                      </div>
                    )}
                  </div>
                </Pressable>
              )
            })}
          </div>
          <Button size="lg" onPress={handleConfirm}>
            Confirm
          </Button>
        </div>
      </Drawer.Container>
    </>
  )
}
